import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../store';
import { fetchDivisionStandings, clearStandingsError } from '../../store/slices/standingsSlice';
import { fetchLeague } from '../../store/slices/leagueSlice';
import DivisionStandings from '../../components/league/DivisionStandings';
import { useActiveLeagueTenant } from '../../hooks/useActiveLeagueTenant';

/**
 * StandingsPage
 *
 * Main page for viewing division standings and player rankings.
 * Allows filtering by category and division level, and toggling
 * between team standings and individual player rankings.
 */
const StandingsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { leagueId, seasonId, divisionId } = useParams<{
    leagueId: string;
    seasonId: string;
    divisionId: string;
  }>();

  const { currentLeague } = useSelector((state: RootState) => state.league);
  const { divisionStandings, loading, error } = useSelector((state: RootState) => state.standings);

  // Resolve tenant slug from URL leagueId — gates data fetches to avoid registry leaks.
  const { resolved: tenantResolved } = useActiveLeagueTenant(leagueId);

  useEffect(() => {
    if (leagueId && tenantResolved) {
      dispatch(fetchLeague(leagueId));
    }
  }, [dispatch, leagueId, tenantResolved]);

  useEffect(() => {
    if (leagueId && seasonId && divisionId && tenantResolved) {
      dispatch(fetchDivisionStandings({ leagueId, seasonId, divisionId }));
    }
  }, [dispatch, leagueId, seasonId, divisionId, tenantResolved]);

  useEffect(() => {
    return () => {
      dispatch(clearStandingsError());
    };
  }, [dispatch]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <svg
          className="animate-spin h-10 w-10 text-primary-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="rounded-md bg-danger-50 p-4">
          <p className="text-sm text-danger-800">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb */}
      <nav className="flex mb-6" aria-label="Breadcrumb">
        <ol className="flex items-center space-x-2">
          <li>
            <Link to="/leagues" className="text-gray-400 hover:text-gray-500 text-sm">
              Leagues
            </Link>
          </li>
          <li className="text-gray-400">/</li>
          {leagueId && (
            <>
              <li>
                <Link
                  to={`/leagues/${leagueId}`}
                  className="text-gray-400 hover:text-gray-500 text-sm"
                >
                  {currentLeague?.name || 'League'}
                </Link>
              </li>
              <li className="text-gray-400">/</li>
            </>
          )}
          <li>
            <span className="text-gray-700 text-sm font-medium">Standings</span>
          </li>
        </ol>
      </nav>

      {/* Page title */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Division Standings</h1>
        {divisionStandings && (
          <p className="mt-1 text-sm text-gray-500">
            {divisionStandings.divisionName} &bull; {divisionStandings.category} &bull; Level{' '}
            {divisionStandings.level}
          </p>
        )}
      </div>

      {/* Standings */}
      {divisionStandings ? (
        <DivisionStandings standings={divisionStandings} />
      ) : (
        <div className="text-center py-12 text-gray-500">No standings data available.</div>
      )}
    </div>
  );
};

export default StandingsPage;
